import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const LanguageSwitcher = ({ languages, translations, currentLanguage }) => {
  const location = useLocation();

  // Extrae el parámetro "page" si existe
  const getPageQueryString = () => {
    const params = new URLSearchParams(location.search);
    return params.has('page') ? `?page=${params.get('page')}` : '';
  };

  // Determina si estamos en la home (o si la URL incluye "legalt")
  const isHomePage = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.includes('legalt')) return true;
    return (
      pathSegments.length === 0 ||
      (pathSegments.length === 1 &&
        languages.some((lang) => lang.code === pathSegments[0]))
    );
  };

  // Construye la URL para un idioma dado
  const constructHref = (language) => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const langCodes = languages.map((lang) => lang.code);

    // Para "es-es": solo existe en la home, apuntamos a "/es-es/legalt/"
    if (language.code === 'es-es') {
      if (isHomePage()) {
        return '/es-es/legalt/';
      }
      return null;
    }

    // Si la URL contiene "legalt", la tratamos como home
    if (pathSegments.includes('legalt')) {
      return language.code === 'es' ? '/' : `/${language.code}/`;
    }

    // Caso: home (sin path o solo el código de idioma)
    if (
      pathSegments.length === 0 ||
      (pathSegments.length === 1 && langCodes.includes(pathSegments[0]))
    ) {
      return language.code === 'es' ? '/' : `/${language.code}/`;
    }

    // Detecta si estamos en /blog/...
    let isBlogPage = false;
    let blogIndex = -1;
    if (pathSegments[0] === 'blog') {
      isBlogPage = true;
      blogIndex = 0;
    } else if (langCodes.includes(pathSegments[0]) && pathSegments[1] === 'blog') {
      isBlogPage = true;
      blogIndex = 1;
    }

    // Si estamos en el blog y el idioma es portugués, no generamos URL (ya que ese blog no existe)
    if (isBlogPage && language.code === 'pt') {
      return null;
    }

    // Páginas estáticas (no blog)
    if (!isBlogPage) {
      if (translations && typeof translations === 'object' && !Array.isArray(translations)) {
        const translationPath = translations[language.code] || null;
        if (!translationPath) return null;
        let finalHref =
          language.code === 'es'
            ? `/${translationPath}`
            : `/${language.code}/${translationPath}`;
        if (!finalHref.endsWith('/')) finalHref += '/';
        return finalHref;
      }
      return null;
    }

    // Para el blog:
    const extraSegments = pathSegments.slice(blogIndex + 1);
    if (extraSegments.length === 0) {
      const baseHref = language.code === 'es' ? '/blog/' : `/${language.code}/blog/`;
      return baseHref + getPageQueryString();
    } else {
      if (Array.isArray(translations)) {
        if (language.code === 'es') {
          const foundSpanish = translations.find((t) => t.locale === 'es');
          if (foundSpanish && foundSpanish.slug) {
            const slug = foundSpanish.slug.endsWith('/')
              ? foundSpanish.slug
              : foundSpanish.slug + '/';
            return `/blog/${slug}`;
          } else {
            return '/';
          }
        } else {
          const foundTranslation = translations.find((t) => t.locale === language.code);
          if (!foundTranslation) return '/';
          const slug = foundTranslation.slug.endsWith('/')
            ? foundTranslation.slug
            : foundTranslation.slug + '/';
          return `/${language.code}/blog/${slug}`;
        }
      }
      return null;
    }
  };

  // Base URL de la web
  const baseUrl = process.env.REACT_APP_BASE_URL
    ? process.env.REACT_APP_BASE_URL.replace(/\/$/, '')
    : window.location.origin;

  // Construye la URL actual (selfHref)
  const selfHref = `${
    location.pathname.endsWith('/') ? location.pathname : location.pathname + '/'
  }${getPageQueryString()}`;

  // ──────────────────────────────────────────────────────────────
  // Aquí se intercambian los códigos en el self:
  // Si currentLanguage es 'es' (es‑CO), se mostrará como 'es-ES'
  // Si currentLanguage es 'es-es' (es‑ES), se mostrará como 'es-CO'
  // ──────────────────────────────────────────────────────────────
  const selfHreflang =
    currentLanguage === 'es'
      ? 'es-ES'
      : currentLanguage === 'es-es'
      ? 'es-CO'
      : languages.find((lang) => lang.code === currentLanguage)?.hreflang ||
        currentLanguage;

  const getAlternateHreflang = (lang) => {
    if (lang.code === 'es') {
      return 'es-ES';
    }
    if (lang.code === 'es-es') {
      return 'es-CO';
    }
    return lang.hreflang;
  };

  // Genera la lista de hreflang alternativos (excluye el idioma actual)
  const alternateHreflangs = languages
    .filter((lang) => lang.code !== currentLanguage)
    .map((lang) => {
      const href = constructHref(lang);
      if (!href) return null;
      // Evita meter alternate que apunte a "/" cuando no estamos en home
      if (!isHomePage() && href === '/') return null;
      return {
        hreflang: getAlternateHreflang(lang),
        href: `${baseUrl}${href}`,
      };
    })
    .filter(Boolean);

  // Lista final de links: self + alternates
  const hreflangLinks = [
    { hreflang: selfHreflang, href: `${baseUrl}${selfHref}` },
    ...alternateHreflangs,
  ];

  // x-default apunta a la versión principal (para 'es', que es es‑CO)
  const spanishLang = languages.find((lang) => lang.code === 'es');
  if (spanishLang) {
    const spanishHref = constructHref(spanishLang);
    if (spanishHref && (isHomePage() || spanishHref !== '/')) {
      hreflangLinks.push({
        hreflang: 'x-default',
        href: `${baseUrl}${spanishHref}`,
      });
    }
  }

  return (
    <>
      <Helmet>
        {hreflangLinks.map((link) => (
          <link
            key={link.hreflang}
            rel="alternate"
            hreflang={link.hreflang}
            href={link.href}
            data-rh="true"
          />
        ))}
      </Helmet>

      <div className="language-switcher d-flex">
        {languages.map((lang) => {
          const href =
            lang.code === currentLanguage ? selfHref : constructHref(lang);
          if (!href) return null;
          return (
            <Link key={lang.code} to={href} className="mx-2">
              <img
                src={lang.flag}
                alt={`${lang.name} flag`}
                style={{ width: '22px', height: '18px', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                fetchpriority="high"
              />
            </Link>
          );
        })}
      </div>
    </>
  );
};

LanguageSwitcher.propTypes = {
  languages: PropTypes.array.isRequired,
  translations: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
    .isRequired,
  currentLanguage: PropTypes.string.isRequired,
};

export default LanguageSwitcher;
